import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useScorecardService } from "../contexts/ScorecardServiceContext";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useAuthenticationService } from "../../../services/authentication.service";
import ToastService from "../../../services/toast.service";
import useFeedbackDialog from "./useFeedbackDialog";
import useGoalDialog from "./useGoalDialog";
import usePreviousMonthDialog from "./usePreviousMonthDialog";
import useManagerMode from "./useManagerMode";
import useScorecardHistory, { INITIAL_HISTORY_SELECTION } from "./useScorecardHistory";
import useSignalRSync from "./useSignalRSync";
import {
  serializeDraftForComparison,
  isMissingValue,
  getBandStatus,
  getBudgetIndicator,
} from "../helpers";

export default function useScorecardData() {
  // ── Services & routing ──
  const service = useScorecardService();
  const auth = useAuthenticationService();
  const { impersonatedUser, isAdmin, isImpersonationReady } = useImpersonation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLoadDone = useRef(false);
  const lastLoadedEmailRef = useRef(undefined);

  const updateSearchParams = useCallback((params, options) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(params);
      for (const key of ["tab", "staff", "impersonate"]) {
        const value = prev.get(key);
        if (value && !next.has(key)) {
          next.set(key, value);
        }
      }
      return next;
    }, options);
  }, [setSearchParams]);

  // Opens a scorecard in the URL. Pushes a new browser history entry when
  // transitioning from the list (no scorecard currently open) so the browser
  // Back button returns to the list instead of leaving the app. Replaces the
  // entry when switching between scorecards so they don't stack up.
  const openScorecardInUrl = useCallback((scorecardId) => {
    const hasScorecardOpen = new URLSearchParams(window.location.search).has("scorecardId");
    updateSearchParams({ scorecardId: String(scorecardId) }, { replace: hasScorecardOpen });
  }, [updateSearchParams]);

  // ── Local state ──
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDataRefreshing, setIsDataRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lastHydratedAt, setLastHydratedAt] = useState(null);
  const [savedDraftSnapshot, setSavedDraftSnapshot] = useState(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [previousMonthGoals, setPreviousMonthGoals] = useState([]);

  // ── Sub-hooks ──
  const {
    scorecardHistory, setScorecardHistory,
    isHistoryLoading,
    historyError, setHistoryError,
    historySelection, setHistorySelection,
    isViewingHistory,
    currentPeriodKpiDataIds,
    loadScorecardHistory,
  } = useScorecardHistory({ service });

  // ── Derived state ──
  const activeDraft = historySelection.scorecard ?? draft;
  const activeLoadError = isViewingHistory ? historySelection.error : loadError;
  const isPanelLoading = isLoading || historySelection.loading;

  const groups = useMemo(() => activeDraft?.groups ?? [], [activeDraft]);

  const totalFields = useMemo(
    () => groups.reduce((count, group) => count + (group.items?.length ?? 0), 0),
    [groups]
  );

  const missingFields = useMemo(
    () => groups.reduce(
      (count, group) => count + (group.items?.filter((item) => isMissingValue(item.value)).length ?? 0),
      0
    ),
    [groups]
  );

  const bandTotals = useMemo(() => {
    const totals = { passed: 0, warning: 0, failed: 0 };
    for (const group of groups) {
      for (const item of group.items ?? []) {
        const status = getBandStatus(item);
        if (status) {
          totals[status]++;
        }
      }
    }
    return totals;
  }, [groups]);

  const budgetTotals = useMemo(() => {
    const totals = { underBudget: 0, overBudget: 0 };
    for (const group of groups) {
      for (const item of group.items ?? []) {
        const indicator = getBudgetIndicator(item);
        if (indicator) {
          if (indicator.status === "on-track") totals.underBudget++;
          else if (indicator.status === "off-track") totals.overBudget++;
        }
      }
    }
    return totals;
  }, [groups]);

  const hasUnsavedChanges = useMemo(
    () => Boolean(draft) && !isViewingHistory && serializeDraftForComparison(draft) !== savedDraftSnapshot,
    [draft, isViewingHistory, savedDraftSnapshot]
  );

  const summaryTone = missingFields > 0 ? "warning" : "success";

  // ── Shared callbacks ──
  const findGoal = useCallback((groupName, itemName, goals = activeDraft?.goals) => (
    (goals ?? []).find((goal) => (
      goal.kpiFocusGroupName === groupName && goal.kpiFocusFieldName === itemName
    ))
  ), [activeDraft]);

  const resetViewState = useCallback(({ clearHistory = false, clearLoadState = false } = {}) => {
    setDraft(null);
    setSavedDraftSnapshot(null);
    setHistorySelection(INITIAL_HISTORY_SELECTION);
    if (clearHistory) setScorecardHistory([]);
    if (clearLoadState) {
      setLoadError(null);
      setLastHydratedAt(null);
    }
  }, []);

  const manager = useManagerMode({
    auth,
    service,
    searchParams,
    updateSearchParams,
    loadScorecardHistory,
    setScorecardHistory,
    resetViewState,
  });

  // Close any open scorecard when the impersonated person changes
  const impersonatedEmail = impersonatedUser?.email ?? null;
  const previousImpersonatedEmailRef = useRef(impersonatedEmail);
  useEffect(() => {
    if (previousImpersonatedEmailRef.current !== impersonatedEmail) {
      previousImpersonatedEmailRef.current = impersonatedEmail;
      resetViewState({ clearLoadState: true });
    }
  }, [impersonatedEmail, resetViewState]);

  const handleGoalCountChanged = useCallback((kpiDataId, delta) => {
    setScorecardHistory((currentHistory) =>
      currentHistory.map((item) =>
        item.kpiDataId === kpiDataId
          ? { ...item, goalCount: Math.max((item.goalCount ?? (delta > 0 ? 0 : 1)) + delta, 0) }
          : item
      )
    );
    if (manager.selectedStaffEmail) {
      manager.setSubordinates((current) =>
        current.map((s) =>
          s.employeeEmail === manager.selectedStaffEmail
            ? { ...s, goalCount: Math.max((s.goalCount ?? (delta > 0 ? 0 : 1)) + delta, 0) }
            : s
        )
      );
    }
  }, [manager.selectedStaffEmail, manager.setSubordinates]);

  const goals = useGoalDialog({
    service,
    activeDraft,
    draft,
    isViewingHistory,
    findGoal,
    setDraft,
    setSavedDraftSnapshot,
    onGoalCountChanged: handleGoalCountChanged,
  });

  const prevMonth = usePreviousMonthDialog({ service, activeDraft, findGoal, isViewingHistory });

  useSignalRSync({
    draft,
    historySelection,
    setDraft,
    setSavedDraftSnapshot,
    setHistorySelection,
    setScorecardHistory,
    setSubordinates: manager.setSubordinates,
    selectedStaffEmail: manager.selectedStaffEmail,
    service,
  });

  // ── Scorecard loading & saving ──
  // Load current scorecard — only fetches the selected month + its goals (2 calls max)
  const loadCurrentScorecard = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    setHistorySelection(INITIAL_HISTORY_SELECTION);
    goals.resetGoalDialog();
    prevMonth.resetPreviousMonthDialog();

    try {
      const emailForLoad = manager.isManager && manager.managerViewMode === "my-team" ? manager.selectedStaffEmail : (manager.isManager && manager.managerViewMode === "my-scorecard" ? manager.managerOwnEmail : undefined);
      lastLoadedEmailRef.current = emailForLoad;
      const scorecard = await service.loadCurrentScorecard(emailForLoad);
      setDraft(scorecard);
      setSavedDraftSnapshot(serializeDraftForComparison(scorecard));
      setDiscardDialogOpen(false);
     
      await loadScorecardHistory(emailForLoad);

      if (scorecard?.kpiDataId) {
        openScorecardInUrl(scorecard.kpiDataId);
      }

       setLastHydratedAt(scorecard?.importedDate ? new Date(scorecard.importedDate) : new Date());

      // Eagerly load previous month goals for focus counts
      if (scorecard?.previousMonthKPIDataId) {
        service.loadGoals(scorecard.previousMonthKPIDataId)
          .then((g) => setPreviousMonthGoals(g))
          .catch(() => setPreviousMonthGoals([]));
      } else {
        setPreviousMonthGoals([]);
      }
    } catch {
      setLoadError("Unable to load the current scorecard. Try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  }, [service, manager.isManager, manager.managerViewMode, manager.managerOwnEmail, manager.selectedStaffEmail, loadScorecardHistory, goals.resetGoalDialog, prevMonth.resetPreviousMonthDialog, openScorecardInUrl]);

  // Refresh current scorecard — re-pulls KPI data using the same employeeEmail used to load it
  const refreshCurrentScorecard = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const scorecard = await service.refreshCurrentScorecard(lastLoadedEmailRef.current);
      setDraft(scorecard);
      setSavedDraftSnapshot(serializeDraftForComparison(scorecard));
      setLastHydratedAt(scorecard?.importedDate ? new Date(scorecard.importedDate) : new Date());

      if (scorecard?.previousMonthKPIDataId) {
        service.loadGoals(scorecard.previousMonthKPIDataId)
          .then((g) => setPreviousMonthGoals(g))
          .catch(() => setPreviousMonthGoals([]));
      } else {
        setPreviousMonthGoals([]);
      }
    } catch {
      setLoadError("Unable to refresh the current scorecard. Try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  }, [service]);

  const triggerDataRefresh = useCallback(async () => {
    setIsDataRefreshing(true);

    try {
      await service.triggerDataRefresh();
      ToastService.success("Data refresh triggered successfully.");
    } catch {
      // Error already logged in service and displayed by API handling
    } finally {
      setIsDataRefreshing(false);
    }
  }, [service]);

  // Load historical scorecard — only fetches scorecard + goals (2 calls max, cached)
  const loadHistoricalScorecard = useCallback(async (historyItem) => {
    const selectedId = historyItem?.kpiDataId;

    if (!selectedId) {
      return;
    }

    setHistorySelection({
      selectedId,
      scorecard: null,
      loading: true,
      error: null,
    });
    setPreviousMonthGoals([]);
    goals.resetGoalDialog();
    prevMonth.resetPreviousMonthDialog();

    try {
      const scorecard = await service.loadHistoricalScorecard(selectedId);

      setHistorySelection({
        selectedId,
        scorecard,
        loading: false,
        error: null,
      });

      if (scorecard?.previousMonthKPIDataId) {
        service.loadGoals(scorecard.previousMonthKPIDataId)
          .then((g) => setPreviousMonthGoals(g))
          .catch(() => setPreviousMonthGoals([]));
      }
    } catch {
      setHistorySelection({
        selectedId,
        scorecard: null,
        loading: false,
        error: "Unable to load the selected historical scorecard.",
      });
    }
  }, [service, goals.resetGoalDialog, prevMonth.resetPreviousMonthDialog]);

  // Load a current-period scorecard by id into the editable draft. Fetching by
  // id (rather than the current endpoint) picks the right centre while keeping
  // the scorecard editable.
  const loadCurrentScorecardById = useCallback(async (historyItem) => {
    const selectedId = historyItem?.kpiDataId;

    if (!selectedId) {
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    setHistorySelection(INITIAL_HISTORY_SELECTION);
    goals.resetGoalDialog();
    prevMonth.resetPreviousMonthDialog();

    try {
      lastLoadedEmailRef.current = historyItem.employeeEmail ?? lastLoadedEmailRef.current;
      const scorecard = await service.loadHistoricalScorecard(selectedId);
      setDraft(scorecard);
      setSavedDraftSnapshot(serializeDraftForComparison(scorecard));
      setDiscardDialogOpen(false);
      setLastHydratedAt(scorecard?.importedDate ? new Date(scorecard.importedDate) : new Date());

      if (scorecard?.previousMonthKPIDataId) {
        service.loadGoals(scorecard.previousMonthKPIDataId)
          .then((g) => setPreviousMonthGoals(g))
          .catch(() => setPreviousMonthGoals([]));
      } else {
        setPreviousMonthGoals([]);
      }
    } catch {
      setLoadError("Unable to load the current scorecard. Try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  }, [service, goals.resetGoalDialog, prevMonth.resetPreviousMonthDialog]);

  const handleHistorySelection = useCallback(async (historyItem) => {
    if (!historyItem?.kpiDataId) {
      return;
    }

    openScorecardInUrl(historyItem.kpiDataId);

    // Current-period cards load into the editable draft; older ones are read-only.
    if (currentPeriodKpiDataIds.has(historyItem.kpiDataId)) {
      await loadCurrentScorecardById(historyItem);
      return;
    }

    await loadHistoricalScorecard(historyItem);
  }, [currentPeriodKpiDataIds, loadCurrentScorecardById, loadHistoricalScorecard, openScorecardInUrl]);

  // Auto-load scorecard from URL param on initial page load
  useEffect(() => {
    if (initialLoadDone.current || !isImpersonationReady || isHistoryLoading || scorecardHistory.length === 0) {
      return;
    }

    initialLoadDone.current = true;
    const scorecardId = searchParams.get("scorecardId");

    if (!scorecardId) {
      return;
    }

    const historyItem = scorecardHistory.find((item) => String(item.kpiDataId) === scorecardId);

    if (historyItem) {
      handleHistorySelection(historyItem);
    } else {
      // The id isn't in the history list (e.g. the current in-progress
      // scorecard) — restore it so a page reload doesn't close the scorecard.
      loadCurrentScorecard();
    }
  }, [isImpersonationReady, isHistoryLoading, scorecardHistory, searchParams, handleHistorySelection, loadCurrentScorecard]);

  // Close the open scorecard when its id is removed from the URL — e.g. the
  // user pressed the browser Back button. This keeps them on the scorecard list
  // inside the app instead of navigating away from it entirely.
  // Read the live URL rather than the router's `searchParams` state: react-router v7
  // applies navigation updates inside a transition, so `searchParams` can briefly lag
  // behind a just-opened scorecard and spuriously close it. `window.location.search`
  // is updated synchronously by history.pushState and is the authoritative source.
  useEffect(() => {
    const liveScorecardId = new URLSearchParams(window.location.search).get("scorecardId");
    if (liveScorecardId || isPanelLoading || isSaving) {
      return;
    }

    if (!draft && !historySelection.selectedId) {
      return;
    }

    goals.resetGoalDialog();
    prevMonth.resetPreviousMonthDialog();
    setHistorySelection(INITIAL_HISTORY_SELECTION);
    setDiscardDialogOpen(false);
    setDraft(null);
    setSavedDraftSnapshot(null);
    setLoadError(null);
    setLastHydratedAt(null);
  }, [searchParams, draft, historySelection.selectedId, isPanelLoading, isSaving, goals.resetGoalDialog, prevMonth.resetPreviousMonthDialog, setHistorySelection]);

  const updateItemValue = useCallback((groupIndex, itemIndex, value) => {
    setDraft((currentDraft) => {
      if (!currentDraft) {
        return currentDraft;
      }

      return {
        ...currentDraft,
        groups: currentDraft.groups.map((group, currentGroupIndex) => {
          if (currentGroupIndex !== groupIndex) {
            return group;
          }

          return {
            ...group,
            items: group.items.map((item, currentItemIndex) => (
              currentItemIndex === itemIndex
                ? { ...item, value }
                : item
            )),
          };
        }),
      };
    });
  }, []);

  const saveScorecard = useCallback(async () => {
    if (!draft || isViewingHistory) {
      return;
    }

    setIsSaving(true);

    try {
      await service.saveScorecard(draft);
      setSavedDraftSnapshot(serializeDraftForComparison(draft));
      ToastService.success("Scorecard saved successfully.");
      await loadScorecardHistory();
    } catch {
      // Error already logged in service
    } finally {
      setIsSaving(false);
    }
  }, [draft, isViewingHistory, service, loadScorecardHistory]);

  // Close / discard
  const closeCurrentScorecard = useCallback(() => {
    goals.resetGoalDialog();
    prevMonth.resetPreviousMonthDialog();
    setDiscardDialogOpen(false);
    setDraft(null);
    setSavedDraftSnapshot(null);
    setLoadError(null);
    setLastHydratedAt(null);
    updateSearchParams({}, { replace: true });
  }, [goals.resetGoalDialog, prevMonth.resetPreviousMonthDialog, updateSearchParams]);

  const closeDiscardDialog = useCallback(() => {
    setDiscardDialogOpen(false);
  }, []);

  const cancelActiveView = useCallback(() => {
    if (isLoading || isSaving || goals.isGoalSaving || historySelection.loading || prevMonth.previousMonthDialog.loading) {
      return;
    }

    if (historySelection.selectedId) {
      goals.resetGoalDialog();
      prevMonth.resetPreviousMonthDialog();
      setHistorySelection(INITIAL_HISTORY_SELECTION);
      updateSearchParams({}, { replace: true });
      return;
    }

    if (hasUnsavedChanges) {
      setDiscardDialogOpen(true);
      return;
    }
    setDraft(null);
    closeCurrentScorecard();
  }, [closeCurrentScorecard, hasUnsavedChanges, historySelection.loading, historySelection.selectedId, goals.isGoalSaving, isLoading, isSaving, prevMonth.previousMonthDialog.loading, goals.resetGoalDialog, prevMonth.resetPreviousMonthDialog, updateSearchParams]);

  const feedback = useFeedbackDialog({ service, activeDraft, setDraft });

  return {
    // Orchestrator state
    draft,
    activeDraft,
    isLoading,
    isSaving,
    isDataRefreshing,
    loadError,
    activeLoadError,
    lastHydratedAt,
    discardDialogOpen,
    previousMonthGoals,

    // Derived
    isViewingHistory,
    isPanelLoading,
    groups,
    totalFields,
    missingFields,
    bandTotals,
    budgetTotals,
    hasUnsavedChanges,
    summaryTone,

    // useScorecardHistory
    isHistoryLoading,
    historyError,
    scorecardHistory,
    historySelection,
    currentPeriodKpiDataIds,

    // useManagerMode
    isAdmin,
    isManager: manager.isManager,
    managerViewMode: manager.managerViewMode,
    subordinates: manager.subordinates,
    isSubordinatesLoading: manager.isSubordinatesLoading,
    selectedStaffEmail: manager.selectedStaffEmail,
    currentUserEmail: manager.currentUserEmail,
    handleStaffSelection: manager.handleStaffSelection,
    switchManagerView: manager.switchManagerView,

    // useGoalDialog
    isGoalSaving: goals.isGoalSaving,
    isGoalDeleting: goals.isGoalDeleting,
    goalDialog: goals.goalDialog,
    openGoalDialog: goals.openGoalDialog,
    closeGoalDialog: goals.closeGoalDialog,
    updateGoalFormField: goals.updateGoalFormField,
    saveGoal: goals.saveGoal,
    deleteGoal: goals.deleteGoal,

    // usePreviousMonthDialog
    isReflectionSaving: prevMonth.isReflectionSaving,
    previousMonthDialog: prevMonth.previousMonthDialog,
    openPreviousMonthDialog: prevMonth.openPreviousMonthDialog,
    closePreviousMonthDialog: prevMonth.closePreviousMonthDialog,
    updatePreviousMonthReflectionNotes: prevMonth.updatePreviousMonthReflectionNotes,
    savePreviousMonthReflection: prevMonth.savePreviousMonthReflection,

    // useFeedbackDialog
    isFeedbackSending: feedback.isFeedbackSending,
    isFeedbackDeleting: feedback.isFeedbackDeleting,
    feedbackDialogOpen: feedback.feedbackDialogOpen,
    openFeedbackDialog: feedback.openFeedbackDialog,
    closeFeedbackDialog: feedback.closeFeedbackDialog,
    submitFeedback: feedback.submitFeedback,
    deleteFeedback: feedback.deleteFeedback,

    // Orchestrator actions
    findGoal,
    loadCurrentScorecard,
    refreshCurrentScorecard,
    triggerDataRefresh,
    handleHistorySelection,
    updateItemValue,
    saveScorecard,
    closeCurrentScorecard,
    closeDiscardDialog,
    cancelActiveView,
  };
}
